import React from 'react';
import styled from 'styled-components';
import { assets } from '../../../../assets';
import { formatNumber } from '../../../../helpers/number-formatters';
import {
  PortfolioFinancialInstrument,
  PortfolioFunds,
  SecurityType,
} from '../../../../logic/graphql/types/types.d';
import { FormattedMessage, useIntl } from 'react-intl';

const DataBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 0px 5px;
`;

const HalfDataBlock = styled.div`
  flex: 0.5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 0px 5px;
`;

const Row = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 5px;
`;

const Icon = styled.img`
  width: 40px;
  height: 40px;
`;

const MiniIcon = styled.img`
  width: 10px;
  height: 10px;
`;

const MobileValueWrapper = styled.header`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
`;

const MobileValue = styled.span`
  text-align: right;
  font-weight: bold;
`;

const MobileDataRow = (props: {
  label: string;
  value?: string | number;
  valueClassName?: string;
}): JSX.Element => {
  return (
    <MobileValueWrapper className="text-12">
      <span>{props.label}</span>
      <MobileValue className={props.valueClassName}>{props.value || 'n.a.'}</MobileValue>
    </MobileValueWrapper>
  );
};

const MobilePercentDataRow = (props: { value: number }): JSX.Element => {
  const intl = useIntl();
  return (
    <MobileValueWrapper className="text-12">
      <span>
        <FormattedMessage id={'dashboardPage.instrumentList.listItem.resultLabel'} />
      </span>
      <div className="flex" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <MiniIcon
          src={props.value < 0 ? assets.icons.negative : assets.icons.positive}
          style={{ marginRight: 5 }}
        />
        <MobileValue className={props.value < 0 ? 'text-negative' : 'text-positive'}>
          {formatNumber(props.value)} %
        </MobileValue>
      </div>
    </MobileValueWrapper>
  );
};

export const FinancialInstrumentContent = ({
  instrument,
}: {
  instrument: PortfolioFinancialInstrument;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <>
      <div className="flex-none" style={{ width: 60 }}>
        <Icon src={assets.icons.security_icon} alt="részvény" width={40} height={40} />
      </div>
      <DataBlock>
        <span className="text-12">
          <b>{instrument.name}</b>{' '}
          {
            instrument.type /*instrument.security_type === SecurityType.UnitOrder ? 'befektetési jegy' : 'részvény'*/
          }
        </span>
        <span className="text-10">
          <b>{formatNumber(instrument.count)}</b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.pcs'} />
        </span>
      </DataBlock>
      <DataBlock>
        <span className="text-12">
          <b className="text-blue">{formatNumber(instrument.value, 'HUF')}</b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.value'} />
        </span>
      </DataBlock>
      <DataBlock>
        <span className="text-10">
          <b>
            {formatNumber(instrument.investable)}{' '}
            <FormattedMessage id={'dashboardPage.instrumentList.listItem.pcs'} />
          </b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.investable'} />
        </span>
        <span className="text-10">
          <b>
            {formatNumber(instrument.payable)}{' '}
            <FormattedMessage id={'dashboardPage.instrumentList.listItem.pcs'} />
          </b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.payable'} />
        </span>
        <span className="text-10">
          <b>
            {formatNumber(instrument.reserved)}{' '}
            <FormattedMessage id={'dashboardPage.instrumentList.listItem.pcs'} />
          </b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.reserved'} />
        </span>
      </DataBlock>
      <HalfDataBlock>
        <div className="flex" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <span className="text-12">
            <FormattedMessage id={'dashboardPage.instrumentList.listItem.resultLabel'} />
          </span>
        </div>
        <div className="flex" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <MiniIcon
            src={instrument.percent < 0 ? assets.icons.negative : assets.icons.positive}
            style={{ marginRight: 5 }}
          />
          <span className={`text-12 ${instrument.percent < 0 ? 'text-negative' : 'text-positive'}`}>
            {formatNumber(instrument.percent)} %
          </span>
        </div>
      </HalfDataBlock>
      <DataBlock className="items-end">
        <span className="text-10 text-end">
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.lastClosingPriceLabel'} />{' '}
          <b>
            {formatNumber(instrument.last_closing_price, instrument.security.currency.currency, 6)}
          </b>
        </span>
        <span className="text-10 text-end">
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.marketPriceLabel'} />{' '}
          <b>{formatNumber(instrument.market_price, instrument.security.currency.currency, 6)}</b>
        </span>
        <span className="text-10 text-end">
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.exchangeRateLabel'} />{' '}
          <b>{formatNumber(instrument.exchange_rate)} HUF</b>
        </span>
      </DataBlock>
    </>
  );
};

export const FinancialInstrumentMobileContent = ({
  instrument,
}: {
  instrument: PortfolioFinancialInstrument;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <>
      <Row>
        <div className="flex flex-col">
          <span className="text-12">
            <b>{instrument.name}</b>{' '}
            {
              instrument.type /*instrument.security_type === SecurityType.UnitOrder ? 'befektetési jegy' : 'részvény'*/
            }
          </span>
          <span className="text-12">
            <b>{formatNumber(instrument.count)}</b>{' '}
            <FormattedMessage id={'dashboardPage.instrumentList.listItem.pcs'} />
          </span>
        </div>
        <div>
          <Icon src={assets.icons.security_icon} alt="részvény" width={40} height={40} />
        </div>
      </Row>
      <Row>
        <span className="text-12">
          <b className="text-blue">{formatNumber(instrument.value, 'HUF')}</b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.value'} />
        </span>
      </Row>
      <div className="w-full mb-2">
        <MobileDataRow
          label={intl.formatMessage({
            id: 'dashboardPage.instrumentList.listItem.investableLabel',
          })}
          value={`${formatNumber(instrument.investable)} HUF`}
        />
        <MobileDataRow
          label={intl.formatMessage({ id: 'dashboardPage.instrumentList.listItem.payableLabel' })}
          value={`${formatNumber(instrument.payable)} HUF`}
        />
        <MobileDataRow
          label={intl.formatMessage({ id: 'dashboardPage.instrumentList.listItem.reservedLabel' })}
          value={`${formatNumber(instrument.reserved)} HUF`}
        />
      </div>
      <div className="w-full mb-2">
        <MobilePercentDataRow value={instrument.percent} />
      </div>
      <div className="w-full mb-0">
        <MobileDataRow
          label={intl.formatMessage({
            id: 'dashboardPage.instrumentList.listItem.lastClosingPriceLabel',
          })}
          value={formatNumber(
            instrument.last_closing_price,
            instrument.security.currency.currency,
            6,
          )}
        />
        <MobileDataRow
          label={intl.formatMessage({
            id: 'dashboardPage.instrumentList.listItem.marketPriceLabel',
          })}
          value={formatNumber(instrument.market_price, instrument.security.currency.currency, 6)}
        />
        <MobileDataRow
          label={intl.formatMessage({
            id: 'dashboardPage.instrumentList.listItem.exchangeRateLabel',
          })}
          value={`${formatNumber(instrument.exchange_rate)} HUF`}
        />
      </div>
    </>
  );
};

export const FundsContent = ({ funds }: { funds: PortfolioFunds }): JSX.Element => {
  return (
    <>
      <div className="flex-none" style={{ width: 60 }}>
        <Icon src={assets.icons.security_icon} alt="valuta" width={40} height={40} />
      </div>
      <DataBlock>
        <span className="text-12">
          <b>{funds.name}</b>
        </span>
        <span className="text-10">
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.balanceLabel'} />{' '}
          <b>{formatNumber(funds.balance)}</b> {funds.name}
        </span>
      </DataBlock>
      <DataBlock>
        <span className="text-12">
          <b className="text-blue">{formatNumber(funds.value, 'HUF')}</b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.value'} />
        </span>
      </DataBlock>
      <DataBlock>
        <span className="text-10">
          <b>
            {formatNumber(funds.investable)} {funds.name}
          </b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.investable'} />
        </span>
        <span className="text-10">
          <b>
            {formatNumber(funds.payable)} {funds.name}
          </b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.payable'} />
        </span>
        <span className="text-10">
          <b>
            {formatNumber(funds.reserved)} {funds.name}
          </b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.reserved'} />
        </span>
      </DataBlock>
      <DataBlock className="items-end">
        <span className="text-10 text-end">
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.exchangeRateLabel'} />{' '}
          <b>{formatNumber(funds.exchange_rate)} HUF</b>
        </span>
      </DataBlock>
    </>
  );
};

export const FundsMobileContent = ({ funds }: { funds: PortfolioFunds }): JSX.Element => {
  const intl = useIntl();
  return (
    <>
      <Row>
        <div className="flex flex-col">
          <span className="text-12">
            <b>{funds.name}</b>
          </span>
          <span className="text-12">
            <FormattedMessage id={'dashboardPage.instrumentList.listItem.balanceLabel'} />{' '}
            <b>{formatNumber(funds.balance)}</b> {funds.name}
          </span>
        </div>
        <div>
          <Icon src={assets.icons.security_icon} alt="valuta" width={40} height={40} />
        </div>
      </Row>
      <Row>
        <span className="text-12">
          <b className="text-blue">{formatNumber(funds.value, 'HUF')}</b>{' '}
          <FormattedMessage id={'dashboardPage.instrumentList.listItem.value'} />
        </span>
      </Row>
      <div className="w-full mb-2">
        <MobileDataRow
          label={intl.formatMessage({
            id: 'dashboardPage.instrumentList.listItem.investableLabel',
          })}
          value={`${formatNumber(funds.investable)} ${funds.name}`}
        />
        <MobileDataRow
          label={intl.formatMessage({ id: 'dashboardPage.instrumentList.listItem.payableLabel' })}
          value={`${formatNumber(funds.payable)} ${funds.name}`}
        />
        <MobileDataRow
          label={intl.formatMessage({ id: 'dashboardPage.instrumentList.listItem.reservedLabel' })}
          value={`${formatNumber(funds.reserved)} ${funds.name}`}
        />
      </div>
      <div className="w-full mb-0">
        <MobileDataRow
          label={intl.formatMessage({
            id: 'dashboardPage.instrumentList.listItem.exchangeRateLabel',
          })}
          value={`${formatNumber(funds.exchange_rate)} HUF`}
        />
      </div>
    </>
  );
};
